/**
 * D1 Service - Cloudflare D1 Database Client
 * 
 * This service preserves the legacy client interface
 * but calls our Cloudflare Pages Functions API instead.
 */

import { BlogPost } from '@/types/blogTypes';
import { getAuthHeader } from '@/services/authService';

// Base URL for API calls - empty string means relative URLs (same origin)
const API_BASE = '';

/**
 * Fetch all published blog posts
 */
export async function fetchBlogPosts(options?: {
  published?: boolean | "all";
  limit?: number;
  offset?: number;
}): Promise<BlogPost[]> {
  const params = new URLSearchParams();
  
  if (options?.published !== undefined) {
    params.set('published', String(options.published));
  }
  if (options?.limit) {
    params.set('limit', String(options.limit));
  }
  if (options?.offset) {
    params.set('offset', String(options.offset));
  }

  const url = `${API_BASE}/api/blog${params.toString() ? `?${params}` : ''}`;
  
  const needsAuth = options?.published === "all" || options?.published === false;
  const response = await fetch(url, {
    headers: needsAuth ? { ...getAuthHeader() } : undefined,
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch blog posts: ${response.statusText}`);
  }
  
  const posts = await response.json();
  return posts.map((post: any) => ({
    ...post,
    metadata: typeof post.metadata === 'string' ? JSON.parse(post.metadata) : post.metadata,
  }));
}

/**
 * Fetch a single blog post by slug
 */
export async function fetchBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  const response = await fetch(`${API_BASE}/api/blog/${slug}`);
  
  if (response.status === 404) {
    return null;
  }
  
  if (!response.ok) {
    throw new Error(`Failed to fetch blog post: ${response.statusText}`);
  }
  
  const post = await response.json();
  return {
    ...post,
    metadata: typeof post.metadata === 'string' ? JSON.parse(post.metadata) : post.metadata,
  };
}

/**
 * Get random elf name components
 */
export async function fetchRandomElfName(): Promise<{
  prefix: string;
  suffix: string;
  lastName: string;
  fullName: string;
}> {
  const response = await fetch(`${API_BASE}/api/elf-names`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch elf name: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Get a random elf scene
 */
export async function fetchRandomElfScene(): Promise<{
  id: string;
  title: string;
  description: string;
  setup_instructions: string;
  required_items: string[];
  difficulty: string;
  time_required: string | null;
} | null> {
  const response = await fetch(`${API_BASE}/api/elf-scenes`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch elf scene: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Check for URL redirects
 */
export async function checkRedirect(path: string): Promise<{
  targetPath: string;
  statusCode: number;
} | null> {
  const response = await fetch(`${API_BASE}/api/redirects?path=${encodeURIComponent(path)}`);
  
  if (!response.ok) {
    return null;
  }
  
  const data = (await response.json().catch(() => null)) as {
    redirect?: { targetPath: string; statusCode: number } | null;
  } | null;
  return data?.redirect ?? null;
}

/**
 * Generate an AI-powered elf letter
 */
export async function generateElfLetter(params: {
  childName: string;
  elfName?: string;
  specialInstructions?: string;
  templateType?: string;
}): Promise<{
  success: boolean;
  content?: string;
  error?: string;
  letterId?: string;
  clientRequestId?: string;
}> {
  const response = await fetch(`${API_BASE}/api/generate-elf-letter`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
  
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  
  if (!response.ok) {
    return {
      success: false,
      error: typeof data.error === 'string' ? data.error : 'Failed to generate letter',
    };
  }
  
  return {
    success: true,
    content: typeof data.content === 'string' ? data.content : undefined,
    letterId: typeof data.letterId === 'string' ? data.letterId : undefined,
    clientRequestId:
      typeof data.clientRequestId === 'string' ? data.clientRequestId : undefined,
  };
}

/**
 * Send elf letter PDF via email
 */
export async function sendElfLetterPdf(params: {
  letterId: string;
  email: string;
  clientRequestId?: string;
  pdfBase64: string;
  childName?: string;
  letterContent?: string;
  templateType?: string;
  marketingOptIn?: boolean;
  marketingOfferVersion?: string;
}): Promise<{
  success: boolean;
  remainingFreeLetters?: number;
  limitReached?: boolean;
  alreadySent?: boolean;
  marketingConfirmationRequired?: boolean;
  error?: string;
}> {
  const response = await fetch(`${API_BASE}/api/send-elf-letter-pdf`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    return {
      success: false,
      remainingFreeLetters:
        typeof data.remainingFreeLetters === 'number' ? data.remainingFreeLetters : undefined,
      limitReached: typeof data.limitReached === 'boolean' ? data.limitReached : undefined,
      marketingConfirmationRequired:
        typeof data.marketingConfirmationRequired === 'boolean'
          ? data.marketingConfirmationRequired
          : undefined,
      error: typeof data.error === 'string' ? data.error : 'Failed to send letter',
    };
  }

  return {
    success: true,
    remainingFreeLetters:
      typeof data.remainingFreeLetters === 'number' ? data.remainingFreeLetters : undefined,
    limitReached: typeof data.limitReached === 'boolean' ? data.limitReached : undefined,
    alreadySent: typeof data.alreadySent === 'boolean' ? data.alreadySent : undefined,
    marketingConfirmationRequired:
      typeof data.marketingConfirmationRequired === 'boolean'
        ? data.marketingConfirmationRequired
        : undefined,
  };
}

/**
 * Track view for analytics (fire and forget)
 */
export async function trackView(postId: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/track-view`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ postId }),
    });
  } catch (error) {
    // Silent failure for analytics
    console.warn('Failed to track view:', error);
  }
}

/**
 * Track tool usage for analytics (fire and forget)
 */
export async function trackToolUsage(params: {
  toolName: string;
  instanceId: string;
  postId?: string;
  metadata?: Record<string, any>;
}): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/track-tool`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
  } catch (error) {
    // Silent failure for analytics
    console.warn('Failed to track tool usage:', error);
  }
}

/**
 * Track CTA clicks
 */
export async function trackClick(params: {
  post_id?: string;
  cta_identifier: string;
}): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/track-click`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
  } catch (error) {
    console.warn('Failed to track click:', error);
  }
}
